<template>
    <ResponsiveDialog
        :value="value"
        @input="$emit('input', $event)"
        :max-width="900"
        :full-height="false"
        framed
        color="warning"
        :bottom-inset="54"
        :header-title="$t('journey.cityTravel.title')"
        header-class="skin-window-header--train"
        card-class="skin-card"
    >
        <div class="travel-dialog-bg skin-window--train">
            <div class="travel-dialog-banner" :style="bannerStyle"/>
            <div class="travel-dialog-steps-row">
                <span class="travel-dialog-steps-pill">
                    {{ transportText }} {{ steps }} <v-icon color="black" size="32" class="ml-0">mdi-shoe-print</v-icon>
                </span>
            </div>
            <div class="travel-dialog-scroll">
                <v-row class="ma-0 pa-2 pt-4 travel-dialog-grid" dense v-if="cities.length > 0">
                    <v-col
                        v-for="c in cities"
                        :key="c.id"
                        cols="4"
                        class="text-center travel-dialog-col"
                    >
                        <NextCircleTile
                            :img="require('@/assets/pic/city/' + c.img)"
                            :title="$t('common.cityNames.' + c.name)"
                            :order="c.level"
                            :disabled="!!c.disabled"
                            :below-label="c.belowLabel || ''"
                            :gym-badge-img="c.badgeId ? require('@/assets/pic/badge/' + c.badgeId + '.png') : ''"
                            :gym-badge-owned="ownsBadge(c.badgeId)"
                            pill-mode
                            @click="select(c.id)"
                        />
                    </v-col>
                </v-row>
                <div v-else class="text-center pa-6 grey--text">{{ $t('journey.cityTravel.noCitiesReachable') }}</div>
            </div>
        </div>
        <ConfirmDialog ref="confirmDialog"/>
    </ResponsiveDialog>
</template>

<script>
    // "Vlak" popup - cestování město->město jako jedno pole gridu, klik otevře
    // popup s výběrem cíle. Data (seznam měst) vlastní a načítá rodič
    // (CityNext.vue), tady se jen zobrazují a vybírají - žádná API logika tady
    // není. Design viz claude/components/journey-city.md.
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
    import ConfirmDialog from '@/components/utils/ConfirmDialog';
    import NextCircleTile from './pieces/NextCircleTile';

    const BIG_CITY_STATIONS = ['Saffron City', 'Celadon City', 'Cerulean City', 'Vermilion City'];

    export default {
        name: 'CityTravelDialog',
        components: {ResponsiveDialog, ConfirmDialog, NextCircleTile},
        props: {
            value: {type: Boolean, default: false},
            cities: {type: Array, default: () => []},
            currentCityName: {type: String, default: ''},
            // Hodnota přichází z rodiče (CityNext.vue minTravelSteps), ne
            // duplicitně natvrdo napsaná tady.
            steps: {type: Number, default: 3},
            // pole vlastněných badge id (trainerPosition.trainer.badges) -
            // řídí barvu/šedivost odznaku vlevo dole na kartičce, nezávisle
            // na `disabled` (viz "You're here" karta aktuálního města)
            trainerBadges: {type: Array, default: () => []},
        },
        computed: {
            bannerStyle() {
                return {backgroundImage: `url(${this.stationImage(this.currentCityName)})`};
            },
            transportText() {
                return this.currentCityName === 'Cinnabar Island' ? this.$t('journey.cityTravel.byShip') : this.$t('journey.cityTravel.byTrain');
            },
        },
        methods: {
            ownsBadge(badgeId) {
                return badgeId !== null && this.trainerBadges.some((badge) => badge.id === badgeId);
            },
            async select(cityId) {
                // Confirm zůstává generický - jen hlavička s Brockem, cena v
                // krocích je vidět na pilulce nad tlačítkem.
                if (await this.$refs.confirmDialog.open('')) {
                    this.$emit('select-city', cityId);
                    this.$emit('input', false);
                }
            },
            stationImage(cityName) {
                if (cityName === 'Cinnabar Island') {
                    return require('@/assets/pic/web/travel/ship-station.png');
                }
                if (BIG_CITY_STATIONS.includes(cityName)) {
                    return require('@/assets/pic/web/travel/train-station-city.png');
                }
                return require('@/assets/pic/web/travel/train-station-town.png');
            },
        },
    }
</script>

<style scoped>
.travel-dialog-bg {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    /* !important nutné - Vuetify má globální pravidlo
       `.v-card > *:last-child:not(.v-btn):not(.v-chip):not(.v-avatar) {
       border-bottom-*-radius: inherit }` s vyšší specificitou, které by
       jinak přebilo prostý `.travel-dialog-bg` selektor; navíc tenhle div
       není skutečně poslední potomek karty (za ním je ConfirmDialog), takže
       se Vuetify pravidlo může chytit i na jiný prvek. */
    border-radius: 0 0 22px 22px !important;
}
/* pozadí řídí skin (public/skins/normal.css, .skin-window--train). */
.travel-dialog-banner {
    flex: none;
    height: 110px;
    /* o 20% užší než karta - obrázek "plave" nad pozadím stejně jako ostatní
       prvky v appce (pilulky, dlaždice), ne na celý spád až k okraji karty. */
    width: 80%;
    margin: 16px auto 0;
    border-radius: 16px;
    background-size: cover;
    background-position: center;
    box-shadow: 0 6px 14px rgba(0,0,0,0.3), 0 2px 5px rgba(0,0,0,0.2);
}
.travel-dialog-steps-row {
    flex: none;
    text-align: center;
    padding: 10px 20px 0 20px;
}
.travel-dialog-steps-pill {
    display: inline-flex;
    align-items: center;
    background: #5C6BC0;
    color: #000;
    font-size: 20px;
    font-weight: 700;
    padding: 4px 14px;
    border-radius: 14px;
    box-shadow: 0 3px 6px rgba(0,0,0,0.25);
}
.travel-dialog-scroll {
    flex-grow: 1;
    overflow-y: auto;
    /* placeholder pro plovoucí oranžový křížek vpravo dole (ResponsiveDialog),
       stejná hodnota jako CityHealDialog.vue/OfficerJennyDialog.vue/TrainerList.vue. */
    padding-bottom: var(--responsive-dialog-close-clearance);
}
.travel-dialog-grid {
    row-gap: 0;
}
.travel-dialog-col {
    padding: 0 4px !important;
}
</style>
